import * as React from 'react';

import { cx } from '../../utils/cx';
import CloseButton from '../Button/CloseButton';
import Tooltip from '../Tooltip';

type CardVariant = 'error' | 'warning' | 'success' | 'brand' | 'mist';

interface ICardVariantStyles {
  bodyBorder: string;
  headerBg: string;
  headerHover: string;
  iconText: string;
}

// mist has no `fg-mist-primary` semantic token — fall back to the utility family.
const VARIANT_STYLES: Record<CardVariant, ICardVariantStyles> = {
  error: {
    bodyBorder: 'border-utility-error-100',
    headerBg: 'bg-utility-error-50',
    headerHover: 'group-hover:bg-utility-error-100',
    iconText: 'text-fg-error-primary',
  },
  warning: {
    bodyBorder: 'border-utility-warning-100',
    headerBg: 'bg-utility-warning-50',
    headerHover: 'group-hover:bg-utility-warning-100',
    iconText: 'text-fg-warning-primary',
  },
  success: {
    bodyBorder: 'border-utility-success-100',
    headerBg: 'bg-utility-success-50',
    headerHover: 'group-hover:bg-utility-success-100',
    iconText: 'text-fg-success-primary',
  },
  brand: {
    bodyBorder: 'border-utility-brand-100',
    headerBg: 'bg-utility-brand-50',
    headerHover: 'group-hover:bg-utility-brand-100',
    iconText: 'text-fg-brand-primary',
  },
  mist: {
    bodyBorder: 'border-utility-mist-100',
    headerBg: 'bg-utility-mist-50',
    headerHover: 'group-hover:bg-utility-mist-100',
    iconText: 'text-utility-mist-600',
  },
};

interface ICardContext {
  variant: CardVariant;
  clickable: boolean;
}

const CardContext = React.createContext<ICardContext | null>(null);

const useCardContext = () => {
  const context = React.useContext(CardContext);
  if (!context) {
    throw new Error('Card compound components must be used within a Card');
  }
  return context;
};

interface ICardProps extends React.PropsWithChildren {
  variant?: CardVariant;
  onClick?: () => void;
  className?: string;
}

const CardComponent = ({ variant = 'warning', onClick, className, children }: ICardProps) => {
  const styles = VARIANT_STYLES[variant];

  return (
    <CardContext.Provider value={{ variant, clickable: !!onClick }}>
      <div
        className={cx(
          'group flex min-w-[232px] flex-col gap-0 overflow-hidden rounded-xl border transition-colors duration-150',
          styles.bodyBorder,
          'bg-primary',
          onClick && 'hover:bg-secondary cursor-pointer',
          className
        )}
        onClick={onClick}
      >
        {children}
      </div>
    </CardContext.Provider>
  );
};

interface IHeaderProps extends React.PropsWithChildren {
  icon?: React.ReactNode;
  label?: string;
  onDismiss?: () => void;
  dismissTooltip?: React.ReactNode;
  className?: string;
}

const Header = ({ icon, label, onDismiss, dismissTooltip, className, children }: IHeaderProps) => {
  const { variant, clickable } = useCardContext();
  const styles = VARIANT_STYLES[variant];

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={cx(
        'flex h-11 items-center justify-between py-2 pl-4 pr-3 transition-colors duration-150',
        styles.headerBg,
        clickable && styles.headerHover,
        className
      )}
    >
      <div className="flex items-center gap-2">
        {icon && (
          <span className={cx('flex size-5 shrink-0 items-center justify-center [&>svg]:size-5', styles.iconText)}>
            {icon}
          </span>
        )}
        {label && <span className="text-primary text-md font-semibold">{label}</span>}
        {children}
      </div>
      {onDismiss && (
        <div
          className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
          onClick={handleCloseClick}
        >
          <Tooltip title={dismissTooltip ?? ''} isDisabled={!dismissTooltip}>
            <CloseButton
              className="hover:bg-transparent"
              size="xs"
              theme="light"
              onPress={onDismiss}
              aria-label="Dismiss"
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

interface IBodyProps extends React.PropsWithChildren {
  className?: string;
}

const Body = ({ className, children }: IBodyProps) => {
  return <div className={cx('flex flex-1 flex-col justify-between overflow-hidden p-4', className)}>{children}</div>;
};

interface ITextProps extends React.PropsWithChildren {
  className?: string;
}

const Text = ({ className, children }: ITextProps) => {
  return <p className={cx('text-secondary text-sm font-medium leading-5', className)}>{children}</p>;
};

export type { CardVariant };

export default Object.assign(CardComponent, {
  Header,
  Body,
  Text,
});
